<script setup>
import { ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'

import 'swiper/css'

const asset = (path) => `${import.meta.env.BASE_URL}${path}`
const swiperInstance = ref(null)
const canSlidePrev = ref(false)
const canSlideNext = ref(true)

const popularGames = [
  { image: 'games/game-1.png', name: 'Royal Joker' },
  { image: 'games/game-2.png', name: 'Rise of Olympus 100' },
  { image: 'games/game-3.png', name: 'Lucky Penny' },
  { image: 'games/game-4.png', name: 'Cherry Max 7s' },
  { image: 'games/game-5.png', name: 'Wolf Fang Ice & Fire' },
  { image: 'games/game-1.png', name: 'Royal Joker Hold and Win' },
  { image: 'games/game-2.png', name: 'Rise of Olympus Bonus' },
  { image: 'games/game-3.png', name: 'Lucky Penny Gold' },
  { image: 'games/game-4.png', name: 'Cherry Max Deluxe' },
  { image: 'games/game-5.png', name: 'Wolf Fang Ice & Fire XL' },
]

function setSwiper(swiper) {
  swiperInstance.value = swiper
  updateNavigation(swiper)
}

function slidePrev() {
  swiperInstance.value?.slidePrev()
}

function slideNext() {
  swiperInstance.value?.slideNext()
}

function updateNavigation(swiper = swiperInstance.value) {
  if (!swiper) return

  canSlidePrev.value = !swiper.isBeginning
  canSlideNext.value = !swiper.isEnd
}
</script>

<template>
  <section class="popular-games" aria-labelledby="popular-games-title">
    <div class="popular-games__inner">
      <div class="popular-games__header">
        <h2 id="popular-games-title" class="popular-games__title">
          <img :src="asset('popular-ico.svg')" alt="" aria-hidden="true" />
          <span>Popular</span>
        </h2>

        <div class="popular-games__actions">
          <button
            class="popular-games__arrow"
            type="button"
            aria-label="Previous popular games"
            :disabled="!canSlidePrev"
            @click="slidePrev"
          >
            <span class="popular-games__arrow-icon popular-games__arrow-icon--prev" aria-hidden="true"></span>
          </button>
          <button
            class="popular-games__arrow"
            type="button"
            aria-label="Next popular games"
            :disabled="!canSlideNext"
            @click="slideNext"
          >
            <span class="popular-games__arrow-icon popular-games__arrow-icon--next" aria-hidden="true"></span>
          </button>
          <button class="popular-games__see-all" type="button">See All</button>
        </div>
      </div>

      <div
        class="popular-games__slider-frame app-swiper-fade"
        :class="{
          'app-swiper-fade--left': canSlidePrev,
          'app-swiper-fade--right': canSlideNext,
        }"
      >
        <Swiper
          class="popular-games__slider"
          :slides-per-view="2.15"
          :space-between="16"
          :breakpoints="{
            520: { slidesPerView: 3.15, spaceBetween: 18 },
            768: { slidesPerView: 4.2, spaceBetween: 20 },
            1024: { slidesPerView: 5.2, spaceBetween: 22 },
            1280: { slidesPerView: 6.05, spaceBetween: 18 },
          }"
          @swiper="setSwiper"
          @slideChange="updateNavigation"
          @resize="updateNavigation"
        >
          <SwiperSlide v-for="game in popularGames" :key="game.name">
            <article class="popular-games__card">
              <img :src="asset(game.image)" :alt="game.name" />
            </article>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </section>
</template>

<style scoped>
.popular-games {
  width: 100%;
  
}

.popular-games__inner {
  width: min(100%, 1280px);
  margin: 0 auto;
  padding: 20px 16px 50px;
  overflow: hidden;
  
}

.popular-games__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 26px;
}

.popular-games__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.popular-games__title img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.popular-games__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.popular-games__arrow,
.popular-games__see-all {
  appearance: none;
  border: 0;
  border-radius: 5px;
  color: #fff;
}

.popular-games__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid transparent;
  background:
    var(--gradient-background) padding-box,
    var(--gradient-border) border-box;
  line-height: 0;
  transition: background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  vertical-align: middle;
}

.popular-games__arrow:disabled {
  cursor: default;
  opacity: 0.55;
}

.popular-games__arrow-icon {
  display: block;
  width: 13px;
  height: 13px;
  border-top: 5px solid currentColor;
  border-right: 5px solid currentColor;
}

.popular-games__arrow-icon--prev {
  transform: translateX(2px) rotate(-135deg);
}

.popular-games__arrow-icon--next {
  transform: translateX(-2px) rotate(45deg);
}

.popular-games__see-all {
  min-width: 96px;
  height: 42px;
  padding: 0 18px;
  background: var(--color-accent);
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.popular-games__arrow:not(:disabled):hover {
  background:
    var(--gradient-background-hover) padding-box,
    var(--gradient-border-hover) border-box;
  box-shadow: var(--shadow-control-hover);
}

.popular-games__see-all:hover {
  background-color: var(--color-accent-hover);
  box-shadow: var(--shadow-accent-hover);
}

.popular-games__slider {
  overflow: visible;
}

.popular-games__slider-frame {
  --app-swiper-fade-offset: 16px;
}

.popular-games__card {
  overflow: hidden;
  aspect-ratio: 153 / 207;
  border: 1px solid rgb(255 211 52 / 0.9);
  border-radius: 5px;
  background: #071536;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.popular-games__card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.22);
  border-color: rgba(255, 211, 52, 1);
}

.popular-games__card img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 520px) {
  .popular-games__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .popular-games__actions {
    width: 100%;
  }

  .popular-games__see-all {
    margin-left: auto;
  }

}

@media (min-width: 768px) {
  .popular-games__inner {
    padding: 28px 48px 66px;
  }

  .popular-games__header {
    margin-bottom: 34px;
  }

  .popular-games__title {
    font-size: 1.5rem;
  }

  .popular-games__title img {
    width: 32px;
    height: 32px;
  }

  .popular-games__arrow {
    width: 44px;
    height: 44px;
  }

  .popular-games__see-all {
    min-width: 110px;
    height: 44px;
    font-size: 1rem;
    font-weight: 500;
  }

  .popular-games__slider-frame {
    --app-swiper-fade-offset: 48px;
  }
}
</style>
